<script setup lang="ts">
import { ref, defineEmits, defineProps } from "vue";
import CustomButton from "@/components/Button/Button.vue";
import DateInput from "@/components/Global/DateInput.vue";
import {useReCaptcha} from "vue-recaptcha-v3";
import { useI18n } from "vue-i18n";
import * as CoreService from "@/services/core.service";
import {trackEvent} from "@/services/event.service";
import {VPhoneInput} from "v-phone-input";
import {parsePhoneNumber} from "awesome-phonenumber";
import {useLocalization} from "@/locales/localization";

const { t } = useI18n();
const {executeRecaptcha, recaptchaLoaded} = useReCaptcha()
const { currentLocale } = useLocalization();


const emit = defineEmits(["close"]);
// States
const boatTypes = ref(["Sailboat", "Catamaran", "Motorboat", "Gulet"]);
const loading = ref(false);
const form = ref({})
const valid = ref(false);
const state = ref<'idle' | 'submit' | 'error'>('idle');
const dialog = ref(false);


const openDialog = () => {
  dialog.value = true;
};
const closeDialog = () => {
  dialog.value = false;
};


const props = defineProps({
  t: Function,
  inputs: Array,
  boatTypes: Array,
  loading: Boolean,
  label: {
    type: String,
    default: "Date*",
  },
  showSpecialRequestText: {
    type: Boolean,
    default: true,
  },
});

const formData = ref({
  destination: "",
  dateStart: null,
  dateEnd: null,
  layout: "",
  passengers: "2",
  cabins: "1",
  name: "",
  telephone: "",
  email: "",
  message: "",
  agreed: false,
});

const inputs = ref([
  { label: t('assistance.items[0].label'), placeholder: t('assistance.items[0].placeholder') },
  { label: t('assistance.items[1].label'), placeholder: t('assistance.items[1].placeholder') },
  { label: t('assistance.items[2].label'), placeholder: t('assistance.items[2].placeholder') },
  { label: t('assistance.items[3].label'), placeholder: t('assistance.items[3].placeholder') },
  { label: t('assistance.items[4].label'), placeholder: t('assistance.items[4].placeholder') },
  { label: t('assistance.items[5].label'), placeholder: t('assistance.items[5].placeholder') },
  { label: t('assistance.items[6].label'), placeholder: t('assistance.items[6].placeholder') },
  { label: t('assistance.items[7].label'), placeholder: t('assistance.items[7].placeholder') },
  { label: t('assistance.items[8].label'), placeholder: t('assistance.items[8].placeholder') },
]);


const rules = {
  required: (value: any) => !!value || props.t('assistance.fillRequired'),
  dateRange: () => {
    return formData.value.dateStart && formData.value.dateEnd
        ? true
        : props.t("assistance.fillRequired");
  },
};


const requestHelp = () => {
  form.value.validate().then(async (result) => {
    if (!result.valid) {
      return;
    }

    formData.value = {
      ...formData.value,
      telephone: parsePhoneNumber(formData.value.telephone).number?.international
    }
    
    trackEvent('cosmosy.ga.yachtPage.sendInquiry', {
      event_label: 'Send Inquiry',

      userData: {
        fullname: formData.value.name,
        email: formData.value.email,
        phone: formData.value.telephone,
        boat_type: formData.value.layout,
        destination: formData.value.destination,
      }

    });

    loading.value = true;

    try {
      await recaptchaLoaded();
      await executeRecaptcha("help_request");

      await CoreService.sendInquiryRequest(formData.value, currentLocale.value);


      state.value = "submit";

      // Clear the form
      Object.keys(formData.value).forEach((key) => {
        if (key === "dateStart" || key === "dateEnd") {
          formData.value[key] = null;
        } else if (typeof formData.value[key] === "boolean") {
          formData.value[key] = false;
        } else {
          formData.value[key] = "";
        }
      });

      form.value.reset();

      setTimeout(() => {
        state.value = "idle";
      }, 5000);
    } catch (error) {
      state.value = "error";
    } finally {
      loading.value = false;
    }
  });
};

</script>

<template>

  <CustomButton
      type="white"
      :rounded="false"
      :outlined="false"
      class="send-request-inquiry-text transition-opacity hover:opacity-60 underline responsive-body-small font-semibold cursor-pointer"
      @click="openDialog"
  >
    {{ t('sendInquiry') }}
  </CustomButton>

  <v-dialog max-width="700" v-model="dialog">
    <template v-slot:default="{ isActive }">
      <v-card class="my-5 c-card relative">
        <div class="sticky top-0 right-0 flex justify-end z-50 pt-10 rounded-t-lg bg-bw-white">
          <button
              class="bg-transparent border-none p-2 hover:opacity-70 transition-opacity"
              @click="closeDialog"
          >
            <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18"/>
          </button>
        </div>
        <v-card-text class="card-padding">

          <h2 class="text-2xl font-bold text-gray-700 my-2">{{ t('assistance.formHeader') }}</h2>
          <p class="text-sm text-gray-500 my-2" v-html="t('assistance.formText')"></p>
          <v-form ref="form" v-model="valid">
            <div class="grid grid-cols-1 gap-4">
              <v-text-field
                  :label="inputs[0].label"
                  v-model="formData.destination"
                  type="text"
                  variant="underlined"
                  :placeholder="inputs[0].placeholder"
                  required
                  :disabled="loading"
                  :rules="[rules.required]"
              ></v-text-field>
            </div>
            <!-- Start Date and Duration -->

            <!-- Start Date and Duration -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateInput
                  v-model:dateFrom="formData.dateStart"
                  v-model:dateTo="formData.dateEnd"
                  :columns="1"
                  :rows="1"
              >
                <template #display="slotProps">
                  <v-text-field
                      :label="label"
                      variant="underlined"
                      required
                      @click="slotProps.toggleOpen"
                      :model-value="slotProps.startString+' - '+slotProps.endString"
                      :rules="[rules.dateRange]"
                  ></v-text-field>
                </template>
              </DateInput>
              <VSelect
                  v-model="formData.layout"
                  :label="inputs[2].label"
                  :items="boatTypes"
                  :placeholder="inputs[2].placeholder"
                  variant="underlined"
                  required
                  :disabled="loading"
                  :rules="[rules.required]"
              />
            </div>

            <!-- People and Cabins -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <v-text-field
                  v-model="formData.passengers"
                  :label="inputs[3].label"
                  type="number"
                  variant="underlined"
                  :placeholder="inputs[3].placeholder"
                  required
                  :disabled="loading"
                  @input="formData.passengers = Math.max(0, formData.passengers)"
                  :rules="[rules.required]"
              ></v-text-field>
              <v-text-field
                  v-model="formData.cabins"
                  :label="inputs[4].label"
                  type="number"
                  variant="underlined"
                  :placeholder="inputs[4].placeholder"
                  required
                  :disabled="loading"
                  @input="formData.cabins = Math.max(0, formData.cabins)"
                  :rules="[rules.required]"
              ></v-text-field>
            </div>

            <!-- Contact Info Section -->
            <div>
              <h3 class="text-lg font-semibold text-gray-700">{{ t('assistance.contactInfo') }}</h3>
            </div>
            <div>
              <v-text-field
                  v-model="formData.name"
                  :label="inputs[5].label"
                  type="text"
                  variant="underlined"
                  :placeholder="inputs[5].placeholder"
                  required
                  :disabled="loading"
                  :rules="[rules.required]"
              ></v-text-field>
            </div>
            <!-- Telephone and Email -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <v-phone-input
                  v-model="formData.telephone"
                  :default-country="'GR'"
                  :invalidMessage = "t('enterValidPhone')"
                  :label= inputs[6].label
                  :only-country-code="false"
                  :return-object="true"
                  required
                  :disabled="loading"
                  density="comfortable"
                  :use-default-validation="false"
                  :rules="[rules.required]"
              />
              <v-text-field
                  v-model="formData.email"
                  :label="inputs[7].label"
                  type="email"
                  variant="underlined"
                  :placeholder="inputs[7].placeholder"
                  required
                  :disabled="loading"
                  :rules="[rules.required]"
              ></v-text-field>
            </div>

            <!-- Message -->
            <div>
              <v-textarea
                  v-model="formData.message"
                  :label="inputs[8].label"
                  variant="underlined"
                  :placeholder="inputs[8].placeholder"
                  rows="4"
                  :disabled="loading"
                  :rules="[rules.required]"
              ></v-textarea>
            </div>

            <!-- Agreement Checkbox -->
            <div>
              <v-checkbox
                  v-model="formData.agreed"
                  :disabled="loading"
                  required
                  :rules="[rules.required]"
              >
                <template v-slot:label>
                  {{ t('assistance.agree') }}&nbsp;
                  <a class="link underline" href="/privacy" target="_blank" @click.stop>
                    {{ t('assistance.privacyPolicy') }}
                  </a> &nbsp;{{ t('assistance.zu') }}.
                </template>
              </v-checkbox>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <div class="flex flex-auto my-3 gap-3">
            <CustomButton type="black"
                          :rounded="true"
                          :outlined="false"
                          :disabled="loading"
                          class="flex-1"
                          @click="requestHelp"
            >
              {{ t('assistance.submit') }}
            </CustomButton>
          </div>
        </v-card-actions>
        <div v-if="state === 'submit'" class="text-green-600 mt-4">
          {{ t('assistance.thanksForMessage') }}
        </div>
        <div v-if="state === 'error'" class="text-red-600 mt-4">
          {{ t('assistance.unexpectedError') }}
        </div>
      </v-card>
    </template>
  </v-dialog>


</template>
<style scoped>
.card-padding {
  padding-left: 5px !important;
  padding-right: 5px !important;
}
@media (max-width: 1640px) {
  .boat-data-header-bottom .send-request-inquiry-text {
    font-size: .75rem;
  }
}
</style>